import { Directive, Input, HostListener, inject } from '@angular/core';
import { NATIVE_TOUCHPOINT_CONTAINER } from '../../app/features/advocacy-group/touchpoint-sheets/native-touchpoint-container.token';
import { evalNgjsObjectLiteral } from './ngjs-object-literal.util';

// WS5 #10 inc 2j: the legacy `FtTargetsModalDirective` ([ft-targets-modal] →
// `$injector.get('ftModal').show(id)`) is DELETED — it was PARASITIC everywhere
// (worktree census): the ft2 `TargetsModalDirective` (account-shared/dialogs/modal,
// SAME selector, in scope wherever AccountSharedModule is) carries every live path —
// native ft2 modals open from its ModalService registry, and unknown ids forward to
// the v1 ftModal via the app.module FT_MODAL bridge (`onModalRequested`). The
// audiences intro modals are NATIVE ft2 ModalComponents (the legacy directive's
// direct v1 show() was a registry MISS → a swallowed TypeError on every click);
// every other usage sits beside an explicit native `(click)` handler. The
// `ft-no-targets-modal-if-task-pending` input had ZERO usages.

/**
 * Native-Angular port of the AngularJS `ftTouchpointTrigger` directive
 * (account-v2/.../components/v1-adapters/touchpoint-container/touchpointTrigger.directive.ts).
 * `<div ft-touchpoint-trigger="{campaignUuid: 'X', propertyUuid: 'Y', name: 'confirmation'}">`
 * — click opens the touchpoint modal via the native `NativeTouchpointContainerService`
 * (createComponent + body-append; WS5 Phase 3 #3), injected through a leaf token so
 * this barrel-declared directive avoids a service import that re-enters the
 * account-shared barrel.
 *
 * The attribute value is an AngularJS object-literal expression (Angular's
 * attribute interpolation resolves the `{{…}}` parts to literals before a
 * user can click). WS5 #10 inc 2z: the `$injector.get('$parse')` grab is
 * gone — `evalNgjsObjectLiteral` replicates `$parse(expr)({})` for the
 * constrained grammar this attribute carries (golden spec + the Advocacy
 * Journeys panel-click suite on p/quebec prove it; a prior JSON.parse drain
 * here silently killed every touchpoint editor, the load gate blind).
 */
@Directive({
    selector: '[ft-touchpoint-trigger]',
    standalone: false
})
export class FtTouchpointTriggerDirective {
  @Input('ft-touchpoint-trigger') optionsExpr!: string;

  /** Native launcher (createComponent mount), resolved via the leaf token; provided
   *  in AdvocacyGroupModule → the `providedIn:'root'` service singleton. */
  private readonly touchpointContainer = inject(NATIVE_TOUCHPOINT_CONTAINER);

  @HostListener('click') onClick(): void {
    const opts = this.parseOptions(this.optionsExpr);
    if (!opts) return;
    this.touchpointContainer.show(opts.campaignUuid, opts.propertyUuid, opts.name, opts.dataType);
  }

  private parseOptions(expr: string | null | undefined): any {
    if (!expr) return null;
    try {
      // The attribute is an AngularJS object-literal EXPRESSION — unquoted keys,
      // single-quoted string values, e.g.
      //   `{campaignUuid: 'E5E…', propertyUuid: '3A2B…', name: 'confirmation'}`.
      // evalNgjsObjectLiteral = `$parse(expr)({})` for exactly that grammar
      // (see the util doc + golden spec); like v1, a syntax error throws and
      // the click is swallowed.
      const parsed = evalNgjsObjectLiteral(expr);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
      return null;
    }
  }
}
